/* callWrapper.ts - Jamie Prince, 2022
 *
 * A simple wrapper for state-changing ethers.js function calls which will fetch the revert message from a node
 * on the event of failures. Multiple options for logging of results, interactively or in a structured manner.
 *
 * Usage:
 *
 *      const abc = new callWrapper( "< alert | console | inclass >", { rpcURL: string , chainId: number } );
 *
 *      abc.call( originalFnCall, "debugMarker");
 *
 *      where originalFnCall is the ethers.js contract function call promise,
 *      and "debugMarker" is an indication for the purpose of debug, since we are unable to fetch that from a Promise.
 *
 *      if using "inclass" for logging, error messages are available after execution by examining abc.logData,
 *      if using "alert" or "console" they are also emitted during runtime.
 *
 *      log messages are in abc.logData (format = [ { Messages: "", res: {}, isSuccess: bool, callID: number } ] )
 *
 */

import { ethers } from "ethers";

export class callWrapper {
    logData: Array<{ callID: number; messages: string; res: any; isSuccess: boolean }> = [];
    callID: number = 0;
    logType: "console" | "alert" | "inclass" = "inclass";
    provider: ethers.providers.JsonRpcProvider | null = null;
    rpcUrl: string | null = null;
    chainId: number = 0;

    /**
     * @param loggingType - How to log results: "console", "alert", or "inclass".
     * @param chainData - Chain configuration.
     * @param chainData.rpcUrl - The RPC URL for the provider.
     * @param chainData.chainId - The chain ID.
     */
    constructor(loggingType: "console" | "alert" | "inclass" = "inclass", chainData: { rpcUrl: string; chainId?: number } = {}) {
        const { rpcUrl, chainId = 0 } = chainData;

        if (!rpcUrl) {
            throw new Error("Initialization error: No RPC URL Provided");
        }

        this.rpcUrl = rpcUrl;
        this.chainId = chainId;
        this.logType = loggingType;
        this.provider = new ethers.providers.JsonRpcProvider(this.rpcUrl);
    }

    /**
     * Logs messages based on the configured logType.
     * @param isSuccess - Whether the operation was successful.
     * @param res - The result or error object.
     * @param messages - The messages to log.
     */
    log = (isSuccess: boolean, res: any, ...messages: string[]): void => {
        const message = messages.join("\n  ");
        this.logData.push({ callID: this.callID, messages: message, res, isSuccess });

        switch (this.logType) {
            default:
            case "console":
                const color = isSuccess ? "\x1B[32m" : "\x1B[31m";
                // eslint-disable-next-line no-console
                console.log(color + message + "\x1B[0m");
                break;
            case "alert":
                alert(message);
                break;
        }
    }

    /**
     * Executes a transaction and handles its lifecycle.
     * @param functionCall - The ethers.js contract function call promise.
     * @param debugMarker - A descriptive marker for the call for logging purposes.
     */
    call = async (functionCall: Promise<ethers.providers.TransactionResponse>, debugMarker: string = "Unnamed Call"): Promise<void> => {
        this.callID++;

        if (!functionCall) {
            this.log(false, { Error: "No function call provided" }, `[${this.callID}] Initialization error: No call data passed`);
            return;
        }

        try {
            const txResponse = await functionCall;
            const receipt = await txResponse.wait(1);
            this.log(true, receipt, `[${this.callID}] ${debugMarker} - Transaction Successful`,
                `Gas Used: ${receipt.gasUsed.toString()}`,
                `Tx Hash: ${receipt.transactionHash}`);

        } catch (error: any) {
            this.log(false, error, `[${this.callID}] ${debugMarker} - Transaction Failed`);

            if (error.receipt) { // Error from .wait() means it was mined but reverted
                this.log(false, error.receipt,
                    `Status: Reverted`,
                    `Reason: ${error.reason}`,
                    `Gas Used: ${error.receipt.gasUsed.toString()}`,
                    `Tx Hash: ${error.receipt.transactionHash}`);
                const detailedReason = await this._getDetailedRevertReason(error.receipt.transactionHash);
                this.log(false, { detailedReason }, `Detailed Revert Reason: "${detailedReason}"`);
            } else { // Error from sending the transaction itself (e.g., user rejection)
                this.log(false, { code: error.code, reason: error.reason },
                    `Status: Failed to Send`,
                    `Code: ${error.code}`,
                    `Reason: ${error.reason}`);
            }
        }
    }

    /**
     * Replays a transaction to get the detailed revert reason from the EVM.
     * @param txHash - The hash of the reverted transaction.
     * @returns The detailed revert reason.
     * @private
     */
    async _getDetailedRevertReason(txHash: string): Promise<string> {
        if (!this.provider) {
            return "No provider available";
        }
        const tx = await this.provider.getTransaction(txHash);
        if (!tx) return "Could not find transaction to replay.";

        try {
            await this.provider.call(tx, { blockTag: tx.blockNumber });
            return "Could not fetch detailed reason: transaction did not revert on replay.";
        } catch (err: any) {
            // Re-create the original code's parsing strategy for the revert message
            if (err.error && typeof err.error.toString === 'function') {
                const errorString = err.error.toString();
                const revertPrefix = "Error: execution reverted: ";
                const message = errorString.split("\n")[0];
                return message.startsWith(revertPrefix) ? message.substring(revertPrefix.length) : message;
            }
            return err.reason || "An unknown error occurred during transaction replay.";
        }
    }
}
